import React, { useState, useCallback } from "react";
import { createPortal } from "react-dom";
import {
  Download,
  FileText,
  Table2,
  ChevronLeft,
  X,
  Check,
  Users,
  Minus,
} from "lucide-react";
import PdfPreviewModal from "../ui/PdfPreviewModal";
import { fmtDate, fmtCurrency } from "../ui";
import { useToast } from "../../contexts/NotificationContext";

// ── Colonnes disponibles ──────────────────────────────────────────────────────

const ALL_COLUMNS = [
  { id: "nom", label: "Nom", get: (e) => e.lastName || "—" },
  { id: "prenom", label: "Prénom", get: (e) => e.firstName || "—" },
  { id: "email", label: "Email", get: (e) => e.email || "—" },
  { id: "telephone", label: "Téléphone", get: (e) => e.phone || "—" },
  {
    id: "structure",
    label: "Structure",
    get: (e) => e.structure?.name || e.structureName || "—",
  },
  { id: "poste", label: "Poste", get: (e) => e.position || "—" },
  { id: "contrat", label: "Contrat", get: (e) => e.contractType || "—" },
  {
    id: "statut",
    label: "Statut",
    get: (e) => (e.status === "active" ? "Actif" : "Inactif"),
  },
  { id: "entree", label: "Date d'entrée", get: (e) => fmtDate(e.startDate) },
  {
    id: "fin",
    label: "Fin de contrat",
    get: (e) => fmtDate(e.cddEndDate || e.endDate),
  },
  {
    id: "heures",
    label: "H/semaine",
    get: (e) => (e.workHours ? `${e.workHours}h` : "—"),
  },
  {
    id: "salaire",
    label: "Salaire brut",
    get: (e) => (e.currentSalary ? fmtCurrency(e.currentSalary) : "—"),
  },
  {
    id: "convention",
    label: "Convention",
    get: (e) => e.conventionCategory || "—",
  },
  {
    id: "echelon",
    label: "Échelon",
    get: (e) =>
      e.conventionEchelon != null ? String(e.conventionEchelon) : "—",
  },
];

const DEFAULT_COLS = [
  "nom",
  "prenom",
  "structure",
  "poste",
  "contrat",
  "statut",
  "entree",
  "salaire",
];

// ── Helpers ───────────────────────────────────────────────────────────────────

function getPrimaryRgb() {
  const raw = getComputedStyle(document.documentElement)
    .getPropertyValue("--color-primary")
    .trim();
  if (!raw) return [20, 184, 166];
  return raw.split(" ").map(Number);
}

function fmtNow() {
  const now = new Date();
  return now.toLocaleDateString("fr-FR");
}

// ── PDF table generation ──────────────────────────────────────────────────────

async function genererPdfListe(employees, cols, structureName) {
  const { jsPDF } = await import("jspdf");
  const { applyPlugin } = await import("jspdf-autotable");
  applyPlugin(jsPDF);

  const [r, g, b] = getPrimaryRgb();
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 12;

  // Header band
  doc.setFillColor(r, g, b);
  doc.rect(0, 0, pageW, 18, "F");
  doc.setFontSize(9);
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.text("SuiviPlus", margin, 7);
  if (structureName) {
    doc.setFont("helvetica", "normal");
    doc.text(structureName, pageW / 2, 7, { align: "center" });
  }
  doc.setFont("helvetica", "normal");
  doc.text(`Exporté le ${fmtNow()}`, pageW - margin, 7, { align: "right" });

  // Title
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.setTextColor(15, 23, 42);
  doc.text("Liste des employés", margin, 30);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(100, 116, 139);
  doc.text(
    `${employees.length} employé${employees.length > 1 ? "s" : ""}`,
    margin,
    36,
  );

  // Table
  const headers = cols.map((c) => c.label);
  const rows = employees.map((emp) => cols.map((c) => c.get(emp)));

  doc.autoTable({
    startY: 41,
    head: [headers],
    body: rows,
    margin: { left: margin, right: margin },
    headStyles: {
      fillColor: [r, g, b],
      textColor: [255, 255, 255],
      fontSize: 8,
      fontStyle: "bold",
      cellPadding: { top: 3, bottom: 3, left: 4, right: 4 },
    },
    bodyStyles: {
      fontSize: 8,
      textColor: [30, 30, 30],
      cellPadding: { top: 2.5, bottom: 2.5, left: 4, right: 4 },
    },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    styles: {
      lineColor: [226, 232, 240],
      lineWidth: 0.15,
      overflow: "ellipsize",
    },
    theme: "grid",
    didDrawPage: (data) => {
      // Footer on every page
      doc.setFontSize(7);
      doc.setTextColor(180, 180, 180);
      doc.text("SuiviPlus · Module RH", margin, pageH - 6);
      doc.text(`Page ${data.pageNumber}`, pageW - margin, pageH - 6, {
        align: "right",
      });
    },
  });

  return doc;
}

// ── CSV generation ────────────────────────────────────────────────────────────

function genererCsv(employees, cols) {
  const header = cols.map((c) => `"${c.label}"`).join(";");
  const rows = employees.map((emp) =>
    cols.map((c) => `"${c.get(emp).replace(/"/g, '""')}"`).join(";"),
  );
  return "﻿" + [header, ...rows].join("\n");
}

// ── Étape 1 : Périmètre ───────────────────────────────────────────────────────

function StepScope({
  employees,
  selected,
  setSelected,
  onAll,
  onSelect,
  onClose,
}) {
  const [mode, setMode] = useState("all");
  const [search, setSearch] = useState("");

  const filtered = employees.filter((e) =>
    `${e.firstName} ${e.lastName} ${e.position || ""}`
      .toLowerCase()
      .includes(search.toLowerCase()),
  );

  const toggleEmp = (id) =>
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  const toggleAll = () =>
    setSelected(
      selected.length === filtered.length ? [] : filtered.map((e) => e.id),
    );

  const allChecked =
    filtered.length > 0 && filtered.every((e) => selected.includes(e.id));
  const someChecked =
    filtered.some((e) => selected.includes(e.id)) && !allChecked;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col max-h-[85vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between px-6 pt-5 pb-4 border-b border-gray-100 flex-shrink-0">
          <div>
            <h3 className="text-lg font-bold text-gray-900">
              Exporter la liste
            </h3>
            <p className="text-sm text-gray-500 mt-0.5">
              Choisissez les employés à inclure.
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
          >
            <X size={18} />
          </button>
        </div>

        {/* Mode selector */}
        <div className="px-6 py-4 flex gap-3 flex-shrink-0">
          <button
            onClick={() => setMode("all")}
            className={`flex-1 flex flex-col items-start gap-1.5 p-4 border-2 rounded-xl text-left transition-all ${mode === "all" ? "border-primary bg-primary/5" : "border-gray-200 hover:border-gray-300"}`}
          >
            <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
              <Users size={16} />
            </div>
            <div className="font-semibold text-sm text-gray-900">
              Tous les employés
            </div>
            <p className="text-xs text-gray-500">
              {employees.length} employé{employees.length > 1 ? "s" : ""} dans
              la liste actuelle.
            </p>
          </button>
          <button
            onClick={() => setMode("select")}
            className={`flex-1 flex flex-col items-start gap-1.5 p-4 border-2 rounded-xl text-left transition-all ${mode === "select" ? "border-primary bg-primary/5" : "border-gray-200 hover:border-gray-300"}`}
          >
            <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
              <Check size={16} />
            </div>
            <div className="font-semibold text-sm text-gray-900">
              Sélection manuelle
            </div>
            <p className="text-xs text-gray-500">
              Cochez les employés à inclure.
            </p>
          </button>
        </div>

        {/* Employee list (selection mode) */}
        {mode === "select" && (
          <>
            <div className="px-6 pb-3 flex-shrink-0">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher…"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <div className="flex-1 overflow-y-auto border-t border-gray-100 mx-6 rounded-xl border mb-4">
              {/* Select all row */}
              <div
                className="flex items-center gap-3 px-4 py-2.5 bg-gray-50 border-b border-gray-100 cursor-pointer hover:bg-gray-100"
                onClick={toggleAll}
              >
                <div
                  className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${allChecked ? "bg-primary border-primary" : someChecked ? "bg-primary/10 border-primary" : "bg-white border-gray-300"}`}
                >
                  {allChecked && (
                    <Check size={10} className="text-white" strokeWidth={3} />
                  )}
                  {someChecked && (
                    <Minus size={10} className="text-primary" strokeWidth={3} />
                  )}
                </div>
                <span className="text-xs font-semibold text-gray-600">
                  Tout sélectionner ({filtered.length})
                </span>
              </div>
              {filtered.map((emp) => {
                const checked = selected.includes(emp.id);
                return (
                  <div
                    key={emp.id}
                    onClick={() => toggleEmp(emp.id)}
                    className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-colors border-b border-gray-50 last:border-0 ${checked ? "bg-primary/5" : "hover:bg-gray-50"}`}
                  >
                    <div
                      className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${checked ? "bg-primary border-primary" : "bg-white border-gray-300"}`}
                    >
                      {checked && (
                        <Check
                          size={10}
                          className="text-white"
                          strokeWidth={3}
                        />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-medium text-gray-800">
                        {emp.lastName} {emp.firstName}
                      </span>
                      <span className="text-xs text-gray-400 ml-2">
                        {emp.position || ""}
                      </span>
                    </div>
                    <span
                      className={`text-xs px-1.5 py-0.5 rounded ${emp.status === "active" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}
                    >
                      {emp.status === "active" ? "Actif" : "Inactif"}
                    </span>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* Footer */}
        <div className="flex-shrink-0 flex items-center gap-3 px-6 py-3 border-t border-gray-100 bg-gray-50 rounded-b-2xl">
          <span className="text-sm text-gray-500 flex-1">
            {mode === "all"
              ? `${employees.length} employé${employees.length > 1 ? "s" : ""}`
              : `${selected.length} sélectionné${selected.length > 1 ? "s" : ""}`}
          </span>
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900"
          >
            Annuler
          </button>
          <button
            onClick={() => (mode === "all" ? onAll() : onSelect())}
            disabled={mode === "select" && selected.length === 0}
            className="flex items-center gap-1.5 px-3.5 py-2 text-sm bg-primary text-white rounded-lg font-medium hover:bg-primary-dark disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            Suivant →
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Étape 2 : Colonnes ────────────────────────────────────────────────────────

function StepColumns({
  selectedCols,
  setSelectedCols,
  onNext,
  onBack,
  onClose,
}) {
  const toggleCol = (id) =>
    setSelectedCols((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id],
    );

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col max-h-[85vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between px-6 pt-5 pb-4 border-b border-gray-100 flex-shrink-0">
          <div>
            <h3 className="text-lg font-bold text-gray-900">
              Colonnes à exporter
            </h3>
            <p className="text-sm text-gray-500 mt-0.5">
              Sélectionnez les données à inclure.
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
          >
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="grid grid-cols-2 gap-px bg-gray-100 rounded-xl overflow-hidden">
            {ALL_COLUMNS.map((col) => {
              const checked = selectedCols.includes(col.id);
              return (
                <div
                  key={col.id}
                  onClick={() => toggleCol(col.id)}
                  className={`flex items-center gap-2.5 px-4 py-3 cursor-pointer transition-colors ${checked ? "bg-primary/5" : "bg-white hover:bg-gray-50"}`}
                >
                  <div
                    className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${checked ? "bg-primary border-primary" : "bg-white border-gray-300"}`}
                  >
                    {checked && (
                      <Check size={10} className="text-white" strokeWidth={3} />
                    )}
                  </div>
                  <span className="text-sm text-gray-800">{col.label}</span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex-shrink-0 flex items-center gap-3 px-6 py-3 border-t border-gray-100 bg-gray-50 rounded-b-2xl">
          <button
            onClick={onBack}
            className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
          >
            <ChevronLeft size={14} /> Retour
          </button>
          <span className="text-sm text-gray-500 flex-1 text-center">
            {selectedCols.length} colonne{selectedCols.length > 1 ? "s" : ""}
          </span>
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900"
          >
            Annuler
          </button>
          <button
            onClick={onNext}
            disabled={selectedCols.length === 0}
            className="flex items-center gap-1.5 px-3.5 py-2 text-sm bg-primary text-white rounded-lg font-medium hover:bg-primary-dark disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            Suivant →
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Étape 3 : Format ──────────────────────────────────────────────────────────

function StepFormat({
  count,
  colCount,
  onPickPdf,
  onPickCsv,
  onBack,
  onClose,
}) {
  const [pick, setPick] = useState("pdf");

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between px-6 pt-5 pb-4 border-b border-gray-100">
          <div>
            <h3 className="text-lg font-bold text-gray-900">
              Format d'exportation
            </h3>
            <p className="text-sm text-gray-500 mt-0.5">
              {count} employé{count > 1 ? "s" : ""} · {colCount} colonne
              {colCount > 1 ? "s" : ""}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-6 py-4 flex gap-3">
          <button
            onClick={() => setPick("pdf")}
            className={`flex-1 flex flex-col items-start gap-2 p-4 border-2 rounded-xl text-left transition-all ${pick === "pdf" ? "border-primary bg-primary/5" : "border-gray-200 hover:border-gray-300"}`}
          >
            <div className="w-9 h-9 rounded-lg bg-red-100 text-red-600 flex items-center justify-center">
              <FileText size={18} />
            </div>
            <div className="font-semibold text-sm text-gray-900">
              Tableau PDF
            </div>
            <p className="text-xs text-gray-500 leading-relaxed">
              Mise en page paysage, tableau imprimable avec en-tête SuiviPlus.
            </p>
          </button>
          <button
            onClick={() => setPick("csv")}
            className={`flex-1 flex flex-col items-start gap-2 p-4 border-2 rounded-xl text-left transition-all ${pick === "csv" ? "border-primary bg-primary/5" : "border-gray-200 hover:border-gray-300"}`}
          >
            <div className="w-9 h-9 rounded-lg bg-green-100 text-green-600 flex items-center justify-center">
              <Table2 size={18} />
            </div>
            <div className="font-semibold text-sm text-gray-900">
              Fichier CSV
            </div>
            <p className="text-xs text-gray-500 leading-relaxed">
              Données tabulaires prêtes pour Excel ou un tableur.
            </p>
          </button>
        </div>

        <div className="flex items-center gap-3 px-6 py-3 border-t border-gray-100 bg-gray-50 rounded-b-2xl">
          <button
            onClick={onBack}
            className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
          >
            <ChevronLeft size={14} /> Retour
          </button>
          <div className="flex-1" />
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900"
          >
            Annuler
          </button>
          <button
            onClick={() => (pick === "pdf" ? onPickPdf() : onPickCsv())}
            className="flex items-center gap-1.5 px-3.5 py-2 text-sm bg-primary text-white rounded-lg font-medium hover:bg-primary-dark transition-colors"
          >
            <Download size={13} /> Générer{" "}
            {pick === "pdf" ? "le PDF" : "le CSV"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Composant principal ───────────────────────────────────────────────────────

export default function EmployeeListExport({ employees, structureName }) {
  const toast = useToast();
  const [phase, setPhase] = useState("idle");
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectedCols, setSelectedCols] = useState(DEFAULT_COLS);
  const [targetEmployees, setTargetEmployees] = useState([]);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [generating, setGenerating] = useState(false);

  const goIdle = useCallback(() => setPhase("idle"), []);

  const cols = ALL_COLUMNS.filter((c) => selectedCols.includes(c.id));

  const handleAll = () => {
    setTargetEmployees(employees);
    setPhase("columns");
  };

  const handleSelect = () => {
    setTargetEmployees(employees.filter((e) => selectedIds.includes(e.id)));
    setPhase("columns");
  };

  const handlePdf = async () => {
    setPhase("idle");
    setGenerating(true);
    try {
      const doc = await genererPdfListe(targetEmployees, cols, structureName);
      setPdfDoc(doc);
      setPhase("pdf");
    } catch {
      toast.error("Impossible de générer le PDF");
    }
    setGenerating(false);
  };

  const handleCsv = () => {
    try {
      const csv = genererCsv(targetEmployees, cols);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `employes_export.csv`;
      a.click();
      URL.revokeObjectURL(url);
      goIdle();
    } catch {
      toast.error("Impossible de générer le CSV");
    }
  };

  return (
    <>
      <button
        onClick={() => {
          setSelectedIds([]);
          setPhase("scope");
        }}
        disabled={generating || employees.length === 0}
        className="inline-flex items-center gap-1.5 px-3 py-2 text-sm border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {generating ? (
          <span className="w-3.5 h-3.5 border-2 border-gray-400/40 border-t-gray-600 rounded-full animate-spin" />
        ) : (
          <Download size={14} />
        )}
        Exporter
      </button>

      {phase === "scope" &&
        createPortal(
          <StepScope
            employees={employees}
            selected={selectedIds}
            setSelected={setSelectedIds}
            onAll={handleAll}
            onSelect={handleSelect}
            onClose={goIdle}
          />,
          document.body,
        )}

      {phase === "columns" &&
        createPortal(
          <StepColumns
            selectedCols={selectedCols}
            setSelectedCols={setSelectedCols}
            onNext={() => setPhase("format")}
            onBack={() => setPhase("scope")}
            onClose={goIdle}
          />,
          document.body,
        )}

      {phase === "format" &&
        createPortal(
          <StepFormat
            count={targetEmployees.length}
            colCount={cols.length}
            onPickPdf={handlePdf}
            onPickCsv={handleCsv}
            onBack={() => setPhase("columns")}
            onClose={goIdle}
          />,
          document.body,
        )}

      {phase === "pdf" &&
        pdfDoc &&
        createPortal(
          <PdfPreviewModal
            doc={pdfDoc}
            fileName="employes_export.pdf"
            onClose={() => {
              setPdfDoc(null);
              goIdle();
            }}
          />,
          document.body,
        )}
    </>
  );
}
